<script setup lang="ts">
import type { DragData } from '@/composables/useDragDrop.ts'

const props = defineProps<{
  /**
   * The target width of the item.
   */
  width: number
  /**
   * The target height of the item.
   */
  height: number
  dragData: DragData
  positionId?: PositionId // The position ID of individual visual signature placements
  placeholder?: boolean
  draggable?: boolean
  removable?: boolean
  clustered?: boolean
  disabled?: boolean
  loading?: boolean
}>()

const emit = defineEmits<{
  remove: []
  zoom: [element: HTMLElement]
}>()

const pageNumber = inject(PAGE_NUMBER, 0)

const isPlaced = pageNumber > 0

const { getColorValue } = useColor()

const quality = inject<Ref<Lowercase<SignatureQuality>>>('quality', ref('ses'))
const qualityColor = computed(() => getColorValue(quality.value))

const itemElement = ref<HTMLElement | null>(null)
const { width: itemWidth, height: itemHeight } = useElementSize(itemElement)

const imageElement = inject(VS_IMAGE, ref(null))

// If this component is used in a zoomable container, the container should provide a zoom reference
const zoomScale = inject(ZOOM_FACTOR, ref(1))

const adjustmentFactor = computed(() => (props.positionId ? 1 / zoomScale.value : 1))

const isZoomable = computed(() => {
  if (!isPlaced || !zoomScale.value) return false

  if (!itemWidth.value || !itemHeight.value) return false

  const widthRatio = itemWidth.value / (props.width / zoomScale.value)
  const heightRatio = itemHeight.value / (props.height / zoomScale.value)

  return Math.max(widthRatio, heightRatio) < 0.5
})

const dragImageElement = computed<HTMLCanvasElement | null>(() => {
  const image = imageElement.value
  if (!image) return null

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  canvas.width = itemWidth.value / adjustmentFactor.value
  canvas.height = itemHeight.value / adjustmentFactor.value
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
  ctx.strokeStyle = qualityColor.value
  ctx.lineWidth = 1
  ctx.strokeRect(0, 0, canvas.width, canvas.height)

  Object.assign(canvas.style, {
    position: 'absolute',
    left: 0,
    bottom: 0,
    zIndex: -1,
    cursor: 'grabbing',
  })

  return canvas
})

const isDraggable = computed(() => props.draggable && !props.loading && !isZoomable.value)

const dragData = computed(() => {
  const data: Omit<PositionedSignatureDragData, 'offsetX' | 'offsetY'> = {
    ...props.dragData,
    width: itemWidth.value ?? props.width,
    height: itemHeight.value ?? props.height,
  }

  if (pageNumber) {
    data.pageNumber = pageNumber
  }

  return data
})

const { isDragging } = useDragDrop(itemElement, isDraggable, dragImageElement, dragData)

const isPlaceholder = computed(() => Boolean(props.placeholder))
</script>

<template>
  <div
    ref="itemElement"
    class="item"
    :class="{
      'item--placeholder': isPlaceholder,
      'item--placed': isPlaced,
      'item--draggable': draggable,
      'item--disabled': isDragging || disabled,
      'item--removable': removable,
      'item--zoomable': isZoomable,
    }"
    :draggable="draggable && !loading && !isZoomable"
  >
    <div class="item__content">
      <slot />
    </div>
    <v-fade-transition>
      <div v-if="clustered" />
      <div v-else-if="isZoomable && loading" class="item__action item__action--zoom item__action--loading">
        <skr-spinner :size="20" light />
      </div>
      <div
        v-else-if="isZoomable"
        class="item__action item__action--zoom"
        @click="itemElement && emit('zoom', itemElement)"
      >
        <v-icon size="16" color="white">custom:zoom</v-icon>
      </div>
      <div v-else-if="removable && !isDragging" class="item__action item__action--remove" @click="emit('remove')">
        <v-icon size="13" color="white">custom:close_x</v-icon>
      </div>
    </v-fade-transition>
  </div>
</template>

<style lang="scss" scoped>
@function getAdjustedSize($size) {
  @return calc(#{$size} * v-bind('adjustmentFactor'));
}

.item {
  background: $c-white;
  user-select: none;
  flex: 1;

  :deep(img) {
    pointer-events: none;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
  }

  &--placed {
    background: rgba(255, 255, 255, 0.5);
    box-shadow:
      0px getAdjustedSize(3px) getAdjustedSize(3px) getAdjustedSize(-2px) rgba(0, 0, 0, 0.2),
      0px getAdjustedSize(3px) getAdjustedSize(4px) 0px rgba(0, 0, 0, 0.14),
      0px getAdjustedSize(1px) getAdjustedSize(8px) 0px rgba(0, 0, 0, 0.12);

    .item__content {
      isolation: isolate;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        backdrop-filter: blur(getAdjustedSize(2px));
        z-index: -1;
      }
    }
  }

  &--placeholder {
    // Use same origin as BE (left bottom) for aligning misfitted placeholders
    align-self: self-end;
    background: repeating-linear-gradient(
      0deg,
      rgba($c-grey-light, 0.75),
      rgba($c-grey-light, 0.75) getAdjustedSize(10px),
      rgba($c-grey-fine, 0.75) getAdjustedSize(10px),
      rgba($c-grey-fine, 0.75) getAdjustedSize(20px)
    );
    border-radius: getAdjustedSize(4px);
    box-shadow: none;

    .item__content {
      &::after {
        backdrop-filter: blur(getAdjustedSize(1px));
      }
    }

    :deep(img) {
      opacity: 0.5;
    }
  }

  &--draggable {
    cursor: grab;
  }

  &--disabled {
    opacity: 0.5;
    filter: grayscale(0.75);
  }

  &--removable {
    position: relative;

    &.item--disabled {
      opacity: 0;
    }
  }

  &--zoomable {
    // Hide content that should only be shown when zoomed in
    background: transparent;
    box-shadow: unset !important;
    cursor: default;

    > *:not(.item__action) {
      transition: opacity 0.3s ease;
      opacity: 0;
      pointer-events: none;
    }
  }

  &__action {
    position: absolute;
    display: grid;
    place-items: center;
    z-index: 20;
    top: -15px;
    right: -15px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 1.75rem;
    color: white;
    transform: scale(v-bind('adjustmentFactor'));
    transform-origin: center center !important;
    background-color: v-bind('qualityColor');

    &::after {
      content: '';
      z-index: 1;
      pointer-events: none;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 50%;
      background-color: currentColor;
      opacity: 0;
      transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
    }

    &:hover {
      &::after {
        opacity: 0.12;
      }
    }

    &--zoom {
      top: -20px;
      right: -20px;
      width: 40px;
      height: 40px;
    }
  }
}
</style>
